/**
 * Shared hook: attach an HLS (or plain) video source to a <video> ref.
 *
 * Features:
 * - sourceUnchanged dedup: skips destroy/recreate when videoUrl+avh haven't
 *   actually changed (prevents flickering on reference-only prop changes).
 * - Optional resetKey: forces a full reload when the key changes, even if the
 *   URL is the same (used by MiniTvViewer for schedule/event switches).
 * - Optional onHlsError callback for fatal HLS.js errors.
 * - Network-error retry (up to HLS_RETRY_MAX times).
 */
import React from 'react';
import Hls from 'hls.js';
import { ensureAvhForVodUrl } from 'features/video/components/mini-tv/hlsAvh';

const HLS_RETRY_MAX = 2;
const HLS_RETRY_DELAY_MS = 1500;

export function useHlsOrSrc(videoRef, videoUrl, avh, resetKey, onHlsError) {
  const lastSourceRef = React.useRef({ videoUrl: null, avhString: '' });
  const lastResetKeyRef = React.useRef(resetKey);

  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const avhString = avh != null ? String(avh) : '';
    const sourceUnchanged =
      lastSourceRef.current.videoUrl === videoUrl &&
      lastSourceRef.current.avhString === avhString;
    const resetChanged = lastResetKeyRef.current !== resetKey;
    lastResetKeyRef.current = resetKey;

    if (resetChanged) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        // ignore
      }
    }

    if (!videoUrl || String(videoUrl).trim() === '') {
      lastSourceRef.current = { videoUrl: null, avhString: '' };
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        // ignore
      }
      return () => {};
    }

    if (videoUrl.includes('m3u8') && Hls.isSupported()) {
      if (sourceUnchanged && !resetChanged) {
        return () => {};
      }
      lastSourceRef.current = { videoUrl, avhString };

      const hlsConfig = { enableWorker: true };
      hlsConfig.xhrSetup = (xhr, url) => {
        const finalUrl = ensureAvhForVodUrl(url, videoUrl, avh);
        if (finalUrl !== url) xhr.open('GET', finalUrl, true);
      };
      if (typeof Request !== 'undefined') {
        hlsConfig.fetchSetup = (context, initParams) => {
          const finalUrl = ensureAvhForVodUrl(context.url, videoUrl, avh);
          return new Request(finalUrl, initParams);
        };
      }
      const hls = new Hls(hlsConfig);
      const retryCountRef = { current: 0 };
      const handleError = (event, data) => {
        if (data?.fatal) {
          const isNetwork = data?.type === Hls.ErrorTypes.NETWORK_ERROR;
          if (isNetwork && retryCountRef.current < HLS_RETRY_MAX) {
            retryCountRef.current += 1;
            setTimeout(() => {
              try {
                hls.loadSource(videoUrl);
              } catch (e) {
                if (typeof onHlsError === 'function') onHlsError(e);
              }
            }, HLS_RETRY_DELAY_MS);
          } else {
            if (typeof onHlsError === 'function') onHlsError(data);
          }
        }
      };
      hls.on(Hls.Events.ERROR, handleError);
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      return () => {
        hls.off(Hls.Events.ERROR, handleError);
        hls.destroy();
      };
    }

    if (sourceUnchanged && !resetChanged) {
      return () => {};
    }
    lastSourceRef.current = { videoUrl, avhString };
    video.src = videoUrl;
    try {
      video.load();
    } catch {
      // ignore
    }
    return () => {};
  }, [videoUrl, videoRef, avh, resetKey, onHlsError]);
}
